import { Review } from '../types/definitions/review'
import { AnsweredQuestion, Question } from '../types/definitions/question'
import { TalentRankSummary } from '../types/definitions/talentRank'
import { QuestionTypeEnum } from '../types/enums/question-type.enum'
import { scoredCategories } from './QuestionTopics'
import { isTalentRankTitle } from './talentRankTitle'
import {
  TalentRankRecommendation,
  determineTalentRankRecommendation,
  findTalentRankOption,
} from './talentRankSuggestion'

const TALENT_RANK_THRESHOLD_RULES: Array<{
  range: string
  recommendation: TalentRankRecommendation
  displayLabel: string
}> = [
  { range: 'Score ≥ 90%', recommendation: '10x', displayLabel: 'Top (10X)' },
  { range: 'Score 83–89%', recommendation: '9x', displayLabel: 'Growth (9X)' },
  { range: 'Score 60–82%', recommendation: 'average', displayLabel: 'Core' },
  { range: 'Score < 60%', recommendation: 'mismatch', displayLabel: 'Mismatch' },
]

export const DEFAULT_TALENT_RANK_FALLBACK_LABELS: Record<TalentRankRecommendation, string> = {
  '10x': '10X',
  '9x': '9X',
  average: 'Core',
  mismatch: 'Mismatch',
}

const TALENT_RANK_OPTION_HINTS = ['10x', '9x', 'top', 'growth', 'core', 'mismatch', 'average']

interface CalculateTalentRankSummaryParams {
  scores: number[]
  options: string[]
  fallbackLabels?: Record<TalentRankRecommendation, string>
}

const clampPercentage = (value: number): number => {
  return Math.round(Math.max(0, Math.min(100, value)))
}

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

const normalize = (value?: string): string => value?.trim().toLowerCase() || ''

const looksLikeTalentRankOptions = (options: string[] = []): boolean => {
  const normalizedOptions = options.map(normalize)
  const matches = TALENT_RANK_OPTION_HINTS.filter(hint => normalizedOptions.some(option => option.includes(hint)))
  return matches.length >= 2
}

export const findTalentRankQuestion = <
  T extends Pick<AnsweredQuestion, 'title' | 'label' | 'topic' | 'type' | 'options' | 'order'>
>(
  questions: T[] = []
): T | undefined => {
  const directMatch = questions.find(question => isTalentRankTitle(question.title) || isTalentRankTitle(question.label))

  if (directMatch) {
    return directMatch
  }

  const fallbackMatches = questions
    .filter(question => question.type === QuestionTypeEnum.SINGLE_SELECTION)
    .filter(question => question.topic?.topicKey === 'final_step')
    .filter(question => Array.isArray(question.options) && looksLikeTalentRankOptions(question.options))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

  return fallbackMatches.at(-1)
}

export const getScoredCategoryNames = <T extends Pick<Question, 'topic' | 'type'>>(questions: T[] = []): string[] => {
  const questionsByTopic = new Map<string, T[]>()

  questions.forEach(question => {
    const topicName = question.topic?.name
    const topicKey = question.topic?.topicKey

    if (!topicName || topicKey === 'final_step') {
      return
    }

    const currentQuestions = questionsByTopic.get(topicName) || []
    questionsByTopic.set(topicName, [...currentQuestions, question])
  })

  const derivedCategories = [...questionsByTopic.entries()]
    .filter(([, topicQuestions]) => {
      const hasScale = topicQuestions.some(question => question.type === QuestionTypeEnum.SCALE)
      const hasNumeric = topicQuestions.some(question => question.type === QuestionTypeEnum.NUMERIC)
      return hasScale && hasNumeric
    })
    .map(([topicName]) => topicName)

  return derivedCategories.length ? derivedCategories : scoredCategories
}

export const calculateTalentRankSummary = ({
  scores,
  options,
  fallbackLabels = DEFAULT_TALENT_RANK_FALLBACK_LABELS,
}: CalculateTalentRankSummaryParams): TalentRankSummary | undefined => {
  if (!scores.length) {
    return undefined
  }

  const total = scores.reduce((sum, score) => sum + score, 0)
  const average = clampPercentage(total / scores.length)
  const recommendation = determineTalentRankRecommendation(scores)
  const suggestionValue = findTalentRankOption(options, recommendation)
  const categoryLabel = suggestionValue ?? fallbackLabels[recommendation]

  const thresholds = TALENT_RANK_THRESHOLD_RULES.map(
    ({ range, recommendation: thresholdRecommendation, displayLabel }) => {
      const optionMatch = findTalentRankOption(options, thresholdRecommendation)
      const value = optionMatch ?? fallbackLabels[thresholdRecommendation]

      return {
        range,
        value,
        label: displayLabel,
      }
    }
  )

  return {
    averageScore: average,
    categoryLabel,
    suggestionValue,
    thresholds,
  }
}

const selectCategoryScore = (answers: AnsweredQuestion[], category: string): number | undefined => {
  const categoryAnswers = answers.filter(answer => answer.topic?.name === category)
  if (!categoryAnswers.length) {
    return undefined
  }

  const numericScoreQuestion = categoryAnswers
    .filter(answer => answer.type === QuestionTypeEnum.NUMERIC)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .pop()

  const directScore = toNumber((numericScoreQuestion as AnsweredQuestion | undefined)?.answer)
  const suggestedScore = toNumber(numericScoreQuestion?.suggestedAnswer)
  const score = [directScore, suggestedScore].find((current): current is number => typeof current === 'number')
  return typeof score === 'number' ? clampPercentage(score) : undefined
}

export const buildTalentRankSummaryFromReview = (
  review: Review,
  categories?: string[]
): TalentRankSummary | undefined => {
  if (!review?.answers?.length) {
    return undefined
  }

  const derivedCategories = categories ?? getScoredCategoryNames(review.answers)

  if (!derivedCategories.length) {
    return undefined
  }

  const scores = derivedCategories.map(category => selectCategoryScore(review.answers, category))

  if (scores.some(score => score === undefined)) {
    return undefined
  }

  const talentRankQuestion = findTalentRankQuestion(review.answers)
  const options = Array.isArray(talentRankQuestion?.options) ? (talentRankQuestion?.options as string[]) : []

  return calculateTalentRankSummary({
    scores: scores as number[],
    options,
    fallbackLabels: DEFAULT_TALENT_RANK_FALLBACK_LABELS,
  })
}
